import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import topicsData from "../data/topics";
import "./Topics.css";

function useIntersectionObserver() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // オプション固定のためdeps空で問題なし
  return [ref, isVisible];
}

function AnimatedBlock({ children, delay = 0 }) {
  const [ref, isVisible] = useIntersectionObserver();
  return (
    <div
      ref={ref}
      className={`detail-block ${isVisible ? "is-visible" : ""}`}
      style={{ "--delay": `${delay}s` }}
    >
      {children}
    </div>
  );
}

export default function TopicsDetail() {
  const { id } = useParams();
  const topic = topicsData.find((e) => e.id === parseInt(id, 10));
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const prevTopic = topicsData.find((e) => e.id === parseInt(id, 10) + 1);
  const nextTopic = topicsData.find((e) => e.id === parseInt(id, 10) - 1);

  if (!topic) {
    return (
      <div className="topics-page topics-not-found">
        <p>トピックスが見つかりません。</p>
        <Link to="/topics" className="detail-back-btn">← 一覧へ戻る</Link>
      </div>
    );
  }

  const formattedDate = new Date(topic.date).toLocaleDateString("ja-JP", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="topics-page">
      <div id="page-top" />

      <div className={`topics-detail-page ${mounted ? "is-mounted" : ""}`}>

        {/* ── ヒーローバー ── */}
        <div className="detail-hero">
          <div className="detail-hero-bg" />
          <div className="detail-hero-content">
            <Link to="/topics" className="detail-breadcrumb">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              トピックス一覧
            </Link>
            <p className="detail-hero-number">#{String(topic.id).padStart(2, "0")}</p>
            <h1 className="detail-hero-title">{topic.title}</h1>
            <time className="detail-hero-date" dateTime={topic.date}>{formattedDate}</time>
          </div>
        </div>

        {/* ── 本文コンテナ ── */}
        <div className="detail-body-wrap">
          <article className="detail-article">

            {/* サマリー */}
            <AnimatedBlock delay={0.1}>
              <div className="detail-summary-card">
                <span className="detail-summary-label">SUMMARY</span>
                <p className="detail-summary-text">{topic.summary}</p>
              </div>
            </AnimatedBlock>

            {/* コンテンツブロック */}
            <div className="detail-content">
              {topic.content.map((block, index) =>
                block.type === "text" ? (
                  <AnimatedBlock key={index} delay={0.05 * index}>
                    <p className="topics-content-text">{block.data}</p>
                  </AnimatedBlock>
                ) : (
                  <AnimatedBlock key={index} delay={0.05 * index}>
                    <div className="detail-img-wrap">
                      <img
                        src={block.data}
                        alt={block.alt || topic.title}
                        className="topics-content-img"
                      />
                    </div>
                  </AnimatedBlock>
                )
              )}
            </div>
          </article>

          {/* ── 前後ナビ ── */}
          <nav className="detail-nav">
            <div className="detail-nav-inner">
              {prevTopic ? (
                <Link to={`/topics/${prevTopic.id}`} className="detail-nav-btn detail-nav-prev">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  <span>
                    <small>前の記事</small>
                    <strong>{prevTopic.title}</strong>
                  </span>
                </Link>
              ) : <div />}

              <Link to="/topics" className="detail-nav-list-btn">
                一覧へ
              </Link>

              {nextTopic ? (
                <Link to={`/topics/${nextTopic.id}`} className="detail-nav-btn detail-nav-next">
                  <span>
                    <small>次の記事</small>
                    <strong>{nextTopic.title}</strong>
                  </span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </Link>
              ) : <div />}
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
}
